import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { PrimaryButton } from '@/components/buttons/PrimaryButton';
import { Checkbox } from '@/components/form/Checkbox/Checkbox';
import { InputItem } from '@/components/form/Input/InputItem';
import { SelectItem } from '@/components/form/Select/SelectItem';
import { TermsConditionsAndPrivacyPolicyLink } from '@/components/form/TermsConditionsAndPrivacyPolicyLink/TermsConditionsAndPrivacyPolicyLink';
import { TextareaItem } from '@/components/form/Textarea/TextareaItem';
import { ScreenSize, useScreenSize } from '@/hooks/useScreenSize';
import { mapLocationsToOptions } from '@/utils/locations';

import {
  DateFlexibility,
  LocationOption,
  MeetingEventSpaceInquiryFormData,
  meetingEventSpaceInquiryFormSchema,
} from './types';

const DateFlexibilityOption = dynamic(
  () =>
    import('./DateFlexibilityOption').then((mod) => mod.DateFlexibilityOption),
  { ssr: false }
);

type MeetingEventSpaceInquiryFormProps = {
  defaultValues?: {
    location?: LocationOption;
    dateFlexibility?: DateFlexibility;
  };
  allLocations: LocationOption[];
  isLoading: boolean;
  onSubmit: (data: MeetingEventSpaceInquiryFormData) => void;
  handleLocationChange?: (sfId: string) => void;
};

export default function MeetingEventSpaceInquiryForm({
  defaultValues,
  allLocations,
  isLoading,
  handleLocationChange,
  onSubmit,
}: MeetingEventSpaceInquiryFormProps) {
  const t = useTranslations('meeting-event-space-inquiry-form.form');
  const screenSize = useScreenSize();

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm<MeetingEventSpaceInquiryFormData>({
    resolver: yupResolver(meetingEventSpaceInquiryFormSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    if (defaultValues?.location != null) {
      setValue('location', defaultValues.location.sfId ?? '');
    }

    if (defaultValues?.dateFlexibility != null) {
      setValue('dateFlexibility', defaultValues.dateFlexibility);
    }
  }, [defaultValues]);

  const locationOptions = mapLocationsToOptions(allLocations);

  const formValues = getValues();

  const dateFlexibilityList = (
    <div className="col-span-4">
      {Object.values(DateFlexibility).map((type) => (
        <DateFlexibilityOption
          key={type}
          active={formValues.dateFlexibility === type}
          onClick={() =>
            setValue('dateFlexibility', type, { shouldValidate: true })
          }
        >
          {t(`date-flexibility.${type}`)}
        </DateFlexibilityOption>
      ))}
    </div>
  );

  return (
    <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-3 mt-6 mb-7 sm:mb-5 sm:grid sm:grid-cols-4">
        <InputItem
          className="col-span-2"
          label={t('firstName')}
          {...register('firstName')}
          error={errors.firstName && t(errors.firstName.message)}
          data-testid="input-firstname"
          required
        />
        <InputItem
          className="col-span-2"
          label={t('lastName')}
          {...register('lastName')}
          error={errors.lastName && t(errors.lastName.message)}
          data-testid="input-lastname"
          required
        />
        <InputItem
          className="col-span-2"
          label={t('email')}
          {...register('email')}
          error={errors.email && t(errors.email.message)}
          data-testid="input-email"
          required
        />
        <InputItem
          className="col-span-2"
          label={t('telephone')}
          {...register('telephone')}
          error={errors.telephone && t(errors.telephone.message)}
          data-testid="input-telephone"
          required
        />
        <InputItem
          className="col-span-2"
          label={t('company')}
          {...register('company')}
          error={errors.company && t(errors.company.message)}
          data-testid="input-company"
          required
        />
        <InputItem
          className="col-span-2"
          label={t('numberAttendees')}
          {...register('numberAttendees')}
          error={errors.numberAttendees && t(errors.numberAttendees.message)}
          data-testid="input-numberAttendees"
          required
        />
        <InputItem
          className="col-span-2 text-black"
          label={t('date')}
          type="date"
          {...register('date')}
          error={errors.date && t(errors.date.message)}
          data-testid="input-date"
          required
        />

        {screenSize <= ScreenSize.SM && dateFlexibilityList}

        <InputItem
          className="col-span-1 text-black"
          label={t('startTime')}
          type="time"
          {...register('startTime')}
          error={errors.startTime && t(errors.startTime.message)}
          data-testid="input-startTime"
          required
        />
        <InputItem
          className="col-span-1 text-black"
          label={t('endTime')}
          type="time"
          {...register('endTime')}
          error={errors.endTime && t(errors.endTime.message)}
          data-testid="input-endTime"
          required
        />

        {screenSize > ScreenSize.SM && dateFlexibilityList}

        <div className="col-span-2">
          <SelectItem
            label={t('location')}
            options={locationOptions}
            {...register('location')}
            data-testid="select-location"
            onChange={(e) =>
              handleLocationChange && handleLocationChange(e.target.value)
            }
            required
          />
        </div>

        <div className="col-span-4">
          <Checkbox
            id="open-to-nearby-locations"
            label={t('open-to-nearby-locations')}
            {...register('openToNearbyLocations')}
            data-testid="open-to-nearby-locations-radio-button"
          />
        </div>

        <p className="col-span-4 text-grey-25 mt-9">
          {t.rich('eventDescription', { strong: (chunks) => <b>{chunks}</b> })}
        </p>
        <TextareaItem
          className="col-span-4"
          placeholder={t('eventDescription-placeholder')}
          {...register('eventDescription')}
          error={errors.eventDescription && t(errors.eventDescription.message)}
          rows={screenSize < ScreenSize.MD ? 4 : 2}
          data-testid="input-eventDescription"
        />
      </div>

      <TermsConditionsAndPrivacyPolicyLink />

      <PrimaryButton
        className="self-end w-full mt-8 sm:w-fit"
        type="submit"
        data-testid="submit-application-form"
        disabled={!isValid}
        isLoading={isLoading}
      >
        {t('submit')}
      </PrimaryButton>
    </form>
  );
}
